/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/prop-types */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { isEmpty, uniqueId, cloneDeep } from 'lodash';
import { Img } from 'ui-kit';
import { REMOVE_ICON } from 'assets/icons';
import { FilterEntity, FilterRetailerEntity, AuthEntity, UserEntity } from '_entities';
import IsAzAccount from 'modules/AccessControl/isAzAccount';
import { FILTER_FIELDS, FILTER_OPTIONS, FILTER_RETAILERS } from '_constants/old/filters';
import { isUserJurisdictions } from 'utils/old/isUserJurisdictions';
import { initialState } from '_entities/old/Filter/filter.reducer';
import { CHIPS_TYPES, DROPDOWN_DATA_TYPES } from './constants';
import * as Styled from './styles';

const { setAllCities } = FilterRetailerEntity.actions;
const { applyFilters, clearAllFilters, setFilterError, setShadowFilter, getJurisdictions } = FilterEntity.actions;
const { getSecGeogs } = UserEntity.selectors;
const MIN_HEIGHT_CHIPS_LIST = 61;

const ChipList = ({
  filterMapping,
  withoutScroll,
  getApi,
  setCurrentPage,
  isPublic,
  hideDelete,
  isFilterModal,
  onCountChange,
  inline,
}) => {
  const [isShowAll, setIsShowAll] = useState(false);
  const [chipsListHeight, setChipsListHeight] = useState(0);
  const [agencies, setAgencies] = useState([]);
  const [jurisdictions, setJurisdictions] = useState([]);
  const { getAgencies } = AuthEntity.selectors;
  const jurisdictionsUserList = useSelector(getAgencies);

  const secGeogsList = useSelector(getSecGeogs);

  const geogOptions = useMemo(
    () =>
      secGeogsList?.map((item) => {
        return {
          label: item.label,
          value: item.value,
        };
      }),
    [secGeogsList],
  );

  const toggleIsShowAll = () => setIsShowAll((prev) => !prev);

  const isChipListDisabled = useMemo(() => chipsListHeight < MIN_HEIGHT_CHIPS_LIST, [chipsListHeight]);

  const {
    filter: { shadowFilters, generalFilters, filterErrors },
  } = useSelector((state) => state);
  const { cities, counties, supervisory } = useSelector((state) => state.oldRetailerView);
  const newLocation = useSelector((state) => state.newFilters.LocationObj);
  const agencyList = useSelector((state) => state.newFilters.agencyObj);
  const userCampaignsList = useSelector((state) => state.newFilters.campaignsObj);
  const userList = useSelector((state) => state.newFilters.userObj);
  const corporationList = useSelector((state) => state.newFilters.corpObj);
  const userId = useSelector((state) => state.auth.userId);
  const userEmail = useSelector((state) => state.auth.email);
  const isAzAccount = IsAzAccount();

  const locationTitles = newLocation?.map((el) => el.title);

  const locationFilters = locationTitles?.reduce(
    (acc, title) => ({
      ...acc,

      [title]: {
        title,
        meta: { dataType: title.toUpperCase() },
        chipsType: CHIPS_TYPES.MULTIDROPDOWN,
      },
    }),
    {},
  );

  const filtersList = { ...filterMapping, ...locationFilters };

  const dispatch = useDispatch();

  const supervisoryOptions = useMemo(
    () =>
      supervisory?.map((item) => ({
        value: item.label.name,
        label: item.label.name,
        id: item.label.id,
      })),
    [supervisory],
  );

  useEffect(() => {
    if (!userList.some((item) => item.label === userEmail)) {
      userList?.push({ value: userId, label: userEmail });
    }
  }, [userList]);

  useEffect(async () => {
    if (isPublic) return;
    try {
      await dispatch(setAllCities());
    } catch {
      // Non-fatal: filters render without the cities list.
    }
  }, []);

  const handleClearAllFilters = () => {
    if (withoutScroll) {
      dispatch(applyFilters(initialState.generalFilters));
      getApi({ filters: initialState.generalFilters, page: 1 });
      setCurrentPage(1);
      return;
    }
    dispatch(clearAllFilters());
  };

  const dropdownValues = useMemo(
    () => ({
      [DROPDOWN_DATA_TYPES.JURISDICTIONS]: geogOptions,
      [DROPDOWN_DATA_TYPES.SUPERVISORY]: supervisoryOptions,
      [DROPDOWN_DATA_TYPES.COUNTRY]: counties,
      [DROPDOWN_DATA_TYPES.AGENCY]: agencyList,
      [DROPDOWN_DATA_TYPES.USER]: userList,
      [DROPDOWN_DATA_TYPES.CORPORATION]: corporationList,
      [DROPDOWN_DATA_TYPES.VENUE]: FILTER_OPTIONS.VENUES_TYPE_OPTIONS,
      [DROPDOWN_DATA_TYPES.CAMPAIGN]: userCampaignsList,
    }),
    [
      geogOptions,
      supervisoryOptions,
      counties,
      agencyList,
      userList,
      corporationList,
      FILTER_OPTIONS.VENUES_TYPE_OPTIONS,
      userCampaignsList,
    ],
  );

  const refList = useRef(null);
  const [chips, setChips] = useState([]);
  const filtersDataObject = withoutScroll ? generalFilters : shadowFilters;

  const chipsMapping = {
    [CHIPS_TYPES.CHECKBOX]: {
      // TODO: need to add the exception for the situation that we don't have value in our mapping
      getValue: (key, value) => {
        if (value && !value.length) return null;
        const checkBoxValue = Array.isArray(value) ? value : [value];
        const result = checkBoxValue.map((chipValue) => ({
          key,
          value: chipValue,
          chipLabel: [filtersList[key].title[chipValue]],
          filterObject: filtersList[key],
        }));
        return result;
      },
    },
    [CHIPS_TYPES.RADIOBUTTON]: {
      // TODO: need to add the exception for the situation that we don't have value in our mapping
      getValue: (key, value) => {
        if (!value) return null;
        const result = {
          key,
          value,
          chipLabel: [filtersList[key].title[value]],
          filterObject: filtersList[key],
        };
        return result;
      },
    },
    [CHIPS_TYPES.RADIODROPDOWN]: {
      // TODO: need to add the exception for the situation that we don't have value in our mapping
      getValue: (key, value) => {
        if (!value) return null;
        let chipLabel = '';
        switch (key) {
          case FILTER_FIELDS.NO_VISIT_TYPE_TYPE: {
            const chipValue = value.replace(' type', '');
            // replace used for value 'any type' because this value will be set to dropdown, and other values must be without word 'type'
            chipLabel = `Retailers with no visits of ${
              isAzAccount ? chipValue?.replace('enforcement', 'inspection') : chipValue
            } type`;
            break;
          }
          case FILTER_FIELDS.VENUES_NEAR_RETAILER:
            chipLabel = `Venues within 1,000’ of retailers`;
            break;
          case FILTER_FIELDS.QUICK_VENUES_TYPE:
            chipLabel = `Schools within 1,000’ of retailers`;
            break;
          case FILTER_FIELDS.ALL_SCHOOLS:
            chipLabel = `All schools`;
            break;
          case FILTER_FIELDS.VIOLATION_COUNT_GRT:
            chipLabel = `Retailers with ${value}+ ${isAzAccount ? 'Inspection' : 'Enforcement'} violations`;
            break;
          case FILTER_FIELDS.MINOR_VIOLATION_COUNT_GRT:
            chipLabel = `Retailers with ${value}+ sale to minor violations within one year of each other`;
            break;
          case FILTER_FIELDS.ATCC: {
            if (value.includes(',')) {
              return value.split(',').map((year) => ({
                key,
                value: year,
                chipLabel: `${FILTER_RETAILERS.ATCC_OPTIONS_LABELS.OPTION_ATCC_CERTAIN_YEAR} ${year}`,
                filterObject: filtersList[key],
              }));
            }
            chipLabel =
              FILTER_RETAILERS.ATCC_OPTIONS_LABELS[value] ||
              `${FILTER_RETAILERS.ATCC_OPTIONS_LABELS.OPTION_ATCC_CERTAIN_YEAR} ${value}`;
            break;
          }
          case FILTER_FIELDS.SYNAR_YEAR:
            chipLabel =
              FILTER_RETAILERS.SYNAR_OPTIONS_LABELS[value] ||
              `${FILTER_RETAILERS.SYNAR_OPTIONS_LABELS.OPTION_SYNAR_CERTAIN_YEAR} ${value}`;
            break;
          default:
            chipLabel = `Retailers with ${value}+ ${isAzAccount ? 'Inspection' : 'Enforcement'} violations`;
            break;
        }
        const result = {
          key,
          value,
          chipLabel,
          filterObject: filtersList[key],
        };
        return result;
      },
    },
    [CHIPS_TYPES.TEXTINPUT]: {
      // TODO: need to add the exception for the situation that we don't have value in our mapping
      getValue: (key, value) => {
        if (!value) return null;
        const addText = filtersList[key]?.addText ? ` ${filtersList[key]?.addText}` : '';
        const result = {
          key,
          value,
          chipLabel: `${filtersList[key].title} ${filtersList[key]?.hideValue ? '' : `: ${value}${addText}`}`,
          filterObject: filtersList[key],
        };
        return result;
      },
    },
    [CHIPS_TYPES.ADVANCED_TEXT]: {
      // Advanced Search text fields: a leading "=" in the stored value marks an Exact match,
      // anything else is a Contains match. Render the operator as a word in the chip — the entry's
      // `exactWord` where the field names it differently ("whole word" on note content).
      getValue: (key, value) => {
        if (!value) return null;
        const isExact = typeof value === 'string' && value.startsWith('=');
        const displayValue = isExact ? value.slice(1) : value;
        if (!displayValue) return null;
        const operator = isExact ? filtersList[key]?.exactWord || 'exact' : 'contains';
        return {
          key,
          value,
          chipLabel: `${filtersList[key].title}: ${operator} ${displayValue}`,
          filterObject: filtersList[key],
        };
      },
    },
    [CHIPS_TYPES.ADVANCED_VALUE]: {
      // Advanced Search `date` / `select` fields: one value under the key. `formatValue` turns it into
      // what the chip shows (an option's label, a formatted date); without it the value is shown as sent.
      getValue: (key, value) => {
        if (!value) return null;
        const { title, formatValue } = filtersList[key];
        const display = (formatValue && formatValue(value)) || value;
        return { key, value, chipLabel: `${title}: ${display}`, filterObject: filtersList[key] };
      },
    },
    [CHIPS_TYPES.QUERYSTRING]: {
      getValue: (key, value) => {
        if (!value.length) return null;
        const newValues = value.split(',').map((item) => ({
          key,
          value: item,
          chipLabel: `${filtersList[key].title}: ${item}`,
          filterObject: filtersList[key],
        }));
        return newValues;
      },
    },
    [CHIPS_TYPES.MULTIDROPDOWN]: {
      // TODO: need to add the exception for the situation that we don't have value in our mapping
      getValue: (key, value, dataType) => {
        let newValue = [];
        const valueArray = Array.isArray(value) ? value : [value];
        const locationField = locationTitles?.find((el) => el === key) || FILTER_FIELDS.CITY;
        switch (key) {
          case FILTER_FIELDS.JURISDICTIONS:
          case FILTER_FIELDS.SUPERVISORY_DISTRICTS:
            newValue = valueArray.reduce((acc, item) => {
              const matchItem = [FILTER_FIELDS.SUPERVISORY_DISTRICTS, FILTER_FIELDS.AGENCIES].includes(key)
                ? +item
                : item;
              const currentItem = dropdownValues[dataType].find((option) => option.id === matchItem);
              return currentItem
                ? acc.concat({
                    key,
                    value: currentItem.value,
                    id: currentItem.id,
                    chipLabel: `${filtersList[key].title}: ${currentItem.value}`,
                    filterObject: filtersList[key],
                  })
                : acc;
            }, []);
            break;
          case FILTER_FIELDS.SEC_GEOGS:
          case FILTER_FIELDS.AGENCY_ID:
          case FILTER_FIELDS.CAMPAIGNS:
          case FILTER_FIELDS.CAMPAIGN_ASSIGNMENTS:
          case FILTER_FIELDS.CORPORATION:
          case FILTER_FIELDS.VISIT_USER:
            newValue = valueArray.reduce((acc, item) => {
              const currentItem = dropdownValues[dataType]?.find(
                (option) => option.value.toLowerCase() === item.toLowerCase(),
              );
              return currentItem
                ? acc.concat({
                    key,
                    value: currentItem.value,
                    id: currentItem.value,
                    chipLabel: `${filtersList[key].title}: ${currentItem.label}`,
                    filterObject: filtersList[key],
                  })
                : acc;
            }, []);
            break;
          case locationField:
            newValue = valueArray.reduce((acc, item) => {
              return acc.concat({
                key,
                value: item,
                id: `${key}:${item}`,
                chipLabel: `${key}: ${
                  newLocation.find((obj) => obj.title === key)?.options.find((el) => el.value === item)?.label
                }`,
                filterObject: { chipsType: 'MULTIDROPDOWN', meta: { dataType: key }, title: key },
              });
            }, []);
            break;
          case FILTER_FIELDS.CITIES:
          case FILTER_FIELDS.LOCATION_COUNTY:
          case FILTER_FIELDS.LOCATION_CITY:
            newValue = valueArray.reduce((acc, item) => {
              const currentItem = dropdownValues[dataType]?.find(
                (option) => option.value.toLowerCase() === item.toLowerCase(),
              );

              return currentItem
                ? acc.concat({
                    key,
                    value: currentItem.value,
                    id: currentItem.id,
                    chipLabel: `${filtersList[key].title}: ${currentItem.label}`,
                    filterObject: filtersList[key],
                  })
                : acc.concat({
                    key,
                    value,
                    id: `${key}:${value}`,
                    chipLabel: `${filtersList[key].title}: ${value}`,
                    filterObject: filtersList[key],
                  });
            }, []);
            break;
          case FILTER_FIELDS.VENUES_TYPE:
            newValue = valueArray.reduce((acc, item) => {
              const currentItem = dropdownValues[dataType]?.find(
                (option) => option.value.toLowerCase() === item.toLowerCase(),
              );
              return currentItem
                ? acc.concat({
                    key,
                    value: currentItem.value,
                    id: currentItem.id,
                    chipLabel: `${filtersList[key].title}: ${currentItem.label}`,
                    filterObject: filtersList[key],
                  })
                : acc;
            }, []);
            break;
          default:
            break;
        }
        return newValue;
      },
    },
    [CHIPS_TYPES.DATETIME]: {
      getValue: (key, value) => {
        const [startDate, endDate] = value.split(',');
        const result = [];
        if (startDate) {
          result.push({
            key,
            value: startDate,
            chipLabel: `${filtersList[key].title.start} ${startDate}`,
            meta: { type: 'startDate' },
            filterObject: filtersList[key],
          });
        }
        if (endDate) {
          result.push({
            key,
            value: endDate,
            chipLabel: `${filtersList[key].title.end} ${endDate}`,
            meta: { type: 'endDate' },
            filterObject: filtersList[key],
          });
        }
        return result;
      },
    },
  };

  useEffect(() => {
    if (!refList.current) return;
    setChipsListHeight(refList.current.getBoundingClientRect().height || 0);
  }, [chips, setChipsListHeight, refList]);

  useEffect(() => {
    if (isEmpty(filterMapping)) return;

    try {
      setChips(
        Object.entries(filtersDataObject)
          .reduce((acc, [key, value]) => {
            const itemTypeObject = filtersList[key];
            if (itemTypeObject && value && value !== 'Any') {
              switch (itemTypeObject.chipsType) {
                case CHIPS_TYPES.CHECKBOX:
                  if (key === FILTER_FIELDS.RESIDENTIAL_LOCATION && !Array.isArray(value)) {
                    const chipsValuesArray = [];
                    const queryValuesArray = value.split(',');
                    queryValuesArray.forEach((residentialValue) => {
                      chipsValuesArray.push(chipsMapping[CHIPS_TYPES.CHECKBOX].getValue(key, residentialValue));
                    });
                    return acc.concat(...chipsValuesArray);
                  }
                  return acc.concat(chipsMapping[CHIPS_TYPES.CHECKBOX].getValue(key, value));
                case CHIPS_TYPES.RADIOBUTTON:
                  return acc.concat(chipsMapping[CHIPS_TYPES.RADIOBUTTON].getValue(key, value));
                case CHIPS_TYPES.RADIODROPDOWN:
                  return acc.concat(chipsMapping[CHIPS_TYPES.RADIODROPDOWN].getValue(key, value));
                case CHIPS_TYPES.TEXTINPUT:
                  return acc.concat(chipsMapping[CHIPS_TYPES.TEXTINPUT].getValue(key, value));
                case CHIPS_TYPES.ADVANCED_TEXT:
                  return acc.concat(chipsMapping[CHIPS_TYPES.ADVANCED_TEXT].getValue(key, value));
                case CHIPS_TYPES.ADVANCED_VALUE:
                  return acc.concat(chipsMapping[CHIPS_TYPES.ADVANCED_VALUE].getValue(key, value));
                case CHIPS_TYPES.MULTIDROPDOWN:
                  return acc.concat(
                    chipsMapping[CHIPS_TYPES.MULTIDROPDOWN].getValue(key, value, itemTypeObject.meta.dataType),
                  );
                case CHIPS_TYPES.DATETIME:
                  return acc.concat(chipsMapping[CHIPS_TYPES.DATETIME].getValue(key, value));
                case CHIPS_TYPES.QUERYSTRING:
                  return acc.concat(chipsMapping[CHIPS_TYPES.QUERYSTRING].getValue(key, value));
                default:
                  return acc;
              }
            }
            return acc;
          }, [])
          .flat(),
      );
    } catch {
      // Mapping a malformed filter entry should not crash the chips list.
    }
  }, [
    filtersDataObject,
    jurisdictions,
    supervisoryOptions,
    agencies,
    cities,
    counties,
    newLocation,
    filterMapping,
    dropdownValues,
  ]);

  useEffect(() => {
    (async () => {
      try {
        const [resJurisdictions, resAgencies] = await Promise.all([
          dispatch(getJurisdictions()),
          dispatch(getAgencies()),
        ]);
        const jurisdictionsList = resJurisdictions.then((res) =>
          res.data.map((item) => ({
            value: item.attributes.name,
            label: item.attributes.name,
            id: item.id,
            type: 'AggregationPolygon',
          })),
        );
        const agenciesList = resAgencies.then((res) =>
          res.data.map((item) => ({
            value: item.name,
            label: item.name,
            id: item.id,
            type: 'Agency',
          })),
        );
        setJurisdictions(jurisdictionsList);
        setAgencies(agenciesList);
      } catch {
        // Jurisdictions/agencies fetch failures are non-fatal for the filter modal.
      }
    })();
  }, []);

  const removeDatePickerError = (name) => {
    const newFilterErrors = Object.entries(filterErrors).reduce(
      (acc, [key, errorValue]) => (key === name ? acc : { ...acc, [key]: errorValue }),
      {},
    );
    dispatch(setFilterError(newFilterErrors || {}));
  };

  const deleteCalcpoly = (arr, item) => {
    return Array.isArray(arr) ? arr?.filter((e) => e !== item) : [];
  };

  // TODO: delete func for queryString
  const deleteChips = (item, index) => {
    const updatedFiltersData = { ...filtersDataObject };
    if (item.key === FILTER_FIELDS.HAS_HAD_VISITS_CHECKBOX) {
      updatedFiltersData[FILTER_FIELDS.HAS_HAD_VISITS] = '';
      updatedFiltersData[FILTER_FIELDS.HAS_HAD_VISITS_DATE_RANGE] = '';
      updatedFiltersData[FILTER_FIELDS.HAS_HAD_VISITS_DROP] = '';
    }

    if (item.key === FILTER_FIELDS.NO_VISIT_TYPE_DATE_RANGE) {
      if (isAzAccount) {
        updatedFiltersData[FILTER_FIELDS.NO_VISIT_TYPE] = '';
        updatedFiltersData[FILTER_FIELDS.NO_VISIT_TYPE_TYPE] = '';
        updatedFiltersData[FILTER_FIELDS.NO_VISIT_TYPE_DATE_RANGE] = '';
        updatedFiltersData[FILTER_FIELDS.NO_VISIT_TYPE_DATE_RANGE_LABEL] = '';
      } else {
        updatedFiltersData[FILTER_FIELDS.NO_VISIT_TYPE_DATE_RANGE] = '';
        updatedFiltersData[FILTER_FIELDS.NO_VISIT_TYPE_DATE_RANGE_LABEL] = '';
      }

      const newFilters = Object.entries(updatedFiltersData).reduce(
        (acc, [key, value]) => (isEmpty(value) ? acc : { ...acc, [key]: value }),
        {},
      );

      if (locationTitles?.includes(item.key)) {
        newFilters.calcpoly = deleteCalcpoly(newFilters?.calcpoly, item.value);
      }

      dispatch(applyFilters(newFilters));
      if (withoutScroll) {
        getApi({ filters: newFilters, page: 1 });
        setCurrentPage(1);
      } else {
        dispatch(setShadowFilter(updatedFiltersData));
      }
      return;
    }

    if (item.key === FILTER_FIELDS.NO_VISIT_TYPE_TYPE) {
      updatedFiltersData[FILTER_FIELDS.NO_VISIT_TYPE] = '';
    }

    const setFiltersData = (filtersObject) => {
      const filters = { ...filtersObject };

      const newFilters = Object.entries(filters).reduce(
        (acc, [key, value]) => (isEmpty(value) ? acc : { ...acc, [key]: value }),
        {},
      );
      if (locationTitles?.includes(item.key)) {
        newFilters.calcpoly = deleteCalcpoly(newFilters?.calcpoly, item.value);
      }
      dispatch(applyFilters(newFilters));
      if (withoutScroll) {
        getApi({ filters: newFilters, page: 1 });
        setCurrentPage(1);
      } else {
        dispatch(setShadowFilter(filtersObject));
      }
    };

    switch (item.filterObject.chipsType) {
      case CHIPS_TYPES.CHECKBOX:
        if (item.key === FILTER_FIELDS.CORP_FLAG) {
          // eslint-disable-next-line camelcase
          const cloneSecGeogs = cloneDeep(updatedFiltersData?.jurisdictions || updatedFiltersData?.sec_geogs);
          const whichFilter = updatedFiltersData.jurisdictions ? FILTER_FIELDS.JURISDICTIONS : FILTER_FIELDS.SEC_GEOGS;
          updatedFiltersData[whichFilter] = Array.isArray(cloneSecGeogs)
            ? cloneSecGeogs?.filter((elem) => isUserJurisdictions(elem, jurisdictionsUserList))
            : isUserJurisdictions(cloneSecGeogs, jurisdictionsUserList) && cloneSecGeogs;
        }
        if (item.key === FILTER_FIELDS.RESIDENTIAL_LOCATION && !Array.isArray(updatedFiltersData[item.key])) {
          const newValue = updatedFiltersData[item.key]
            .split(',')
            .filter((qsItem) => qsItem !== item.value)
            .join(',');
          setFiltersData({ ...updatedFiltersData, ...{ [item.key]: newValue } });
        }
        if (Array.isArray(updatedFiltersData[item.key])) {
          const newArray = updatedFiltersData[item.key].filter((qsItem) => qsItem !== item.value);
          setFiltersData({ ...updatedFiltersData, ...{ [item.key]: newArray } });
        } else if (item.value === updatedFiltersData[item.key]) {
          setFiltersData({ ...updatedFiltersData, ...{ [item.key]: null } });
        }
        break;
      case CHIPS_TYPES.MULTIDROPDOWN:
        if (Array.isArray(updatedFiltersData[item.key])) {
          const newArray = updatedFiltersData[item.key].filter((qsItem) => {
            const newTypeQsItem = [FILTER_FIELDS.SUPERVISORY_DISTRICTS, FILTER_FIELDS.AGENCIES].includes(item.key)
              ? +qsItem
              : qsItem;
            return ![item.id, item.value].includes(newTypeQsItem);
          });
          setFiltersData({ ...updatedFiltersData, ...{ [item.key]: newArray } });
        } else {
          setFiltersData({ ...updatedFiltersData, ...{ [item.key]: null } });
        }
        break;
      case CHIPS_TYPES.RADIOBUTTON:
        if (updatedFiltersData[item.key]) {
          setFiltersData({ ...updatedFiltersData, ...{ [item.key]: null } });
        }
        break;
      case CHIPS_TYPES.RADIODROPDOWN: {
        if (updatedFiltersData[item.key]) {
          const currentValue = updatedFiltersData[item.key];
          if (typeof currentValue === 'string' && currentValue.includes(',')) {
            const remaining = currentValue
              .split(',')
              .filter((v) => v !== item.value)
              .join(',');
            setFiltersData({ ...updatedFiltersData, ...{ [item.key]: remaining || null } });
          } else {
            setFiltersData({ ...updatedFiltersData, ...{ [item.key]: null } });
          }
        }
        break;
      }
      case CHIPS_TYPES.TEXTINPUT:
      case CHIPS_TYPES.ADVANCED_TEXT:
      case CHIPS_TYPES.ADVANCED_VALUE:
        if (updatedFiltersData[item.key]) {
          setFiltersData({ ...updatedFiltersData, ...{ [item.key]: null } });
        }
        break;
      case CHIPS_TYPES.DATETIME:
        if (typeof updatedFiltersData[item.key] === 'string') {
          const [startDate, endDate] = updatedFiltersData[item.key].split(',');
          if (startDate && endDate && endDate !== startDate) {
            if (item.key === FILTER_FIELDS.VIOLATION_COUNT_GRT_DATEPICKER_RANGE) {
              setFiltersData({
                ...updatedFiltersData,
                ...{
                  [item.key]: updatedFiltersData[item.key].replace(item.value, ''),
                  violation_count_grt_data_range: updatedFiltersData.violation_count_grt_data_range.replace(
                    item.value,
                    '',
                  ),
                },
              });
            } else {
              setFiltersData({
                ...updatedFiltersData,
                ...{
                  [item.key]: updatedFiltersData[item.key].replace(item.value, ''),
                },
              });
            }
          } else {
            if (startDate) {
              const newDate = updatedFiltersData[item.key].slice(10).replace(',', '');
              if (isEmpty(newDate)) removeDatePickerError(item.key);
              if (item.key === FILTER_FIELDS.VIOLATION_COUNT_GRT_DATEPICKER_RANGE) {
                setFiltersData({
                  ...updatedFiltersData,
                  ...{
                    [item.key]: newDate,
                    violation_count_grt_data_range: newDate,
                  },
                });
              } else {
                setFiltersData({
                  ...updatedFiltersData,
                  ...{
                    [item.key]: newDate,
                  },
                });
              }
            }
            if (endDate) {
              const newDate = updatedFiltersData[item.key].slice(0, -10).replace(',', '');
              if (isEmpty(newDate)) removeDatePickerError(item.key);
              if (item.key === FILTER_FIELDS.VIOLATION_COUNT_GRT_DATEPICKER_RANGE) {
                setFiltersData({
                  ...updatedFiltersData,
                  ...{
                    [item.key]: newDate,
                    violation_count_grt_data_range: newDate,
                  },
                });
              } else {
                setFiltersData({
                  ...updatedFiltersData,
                  ...{
                    [item.key]: newDate,
                  },
                });
              }
            }
          }
        }
        break;
      case CHIPS_TYPES.QUERYSTRING:
        if (updatedFiltersData[item.key]) {
          if (isEmpty(updatedFiltersData[item.key])) {
            setFiltersData({ ...updatedFiltersData, ...{ [item.key]: null } });
          } else {
            const newValues = updatedFiltersData[item.key]
              .split(',')
              .filter((queryItem) => queryItem !== item.value)
              .join(',');
            setFiltersData({ ...updatedFiltersData, ...{ [item.key]: newValues } });
          }
        }
        break;
      default:
    }
    // TODO: it's fast win solution. refactor it
    const els = document.querySelectorAll(`${withoutScroll ? '' : '#filter-body'} #chips`);
    if (els.length <= 1) return;
    const el = els[index + 1];
    if (chips.length > 1 && isEmpty(el)) {
      els[index - 1].focus();
      return;
    }
    el.focus();
  };

  const filterChips = (chipsArray) => {
    const hasDateRangeDate = chipsArray.some((item) => item?.key === 'had_not_visits_in_date_range_date');
    return chipsArray.filter(
      (item) => item !== null && !(hasDateRangeDate && item.key === 'had_not_visits_in_date_range'),
    );
  };

  const filteredChips = filterChips(chips);

  // Report the rendered chip count to interested parents (e.g. a sidebar "N filters" badge) so it
  // always matches what's actually shown here rather than re-deriving the mapping elsewhere.
  useEffect(() => {
    if (onCountChange) onCountChange(filteredChips.length);
  }, [filteredChips.length]);

  // Filter modals always render so the "Selected filtering:" row is visible even with no chips.
  // Every other caller (pages, panels) keeps the old "hide when empty" behavior.
  if (!isFilterModal && !filteredChips.length) return null;

  const hasChips = chips.some((item) => item !== null);

  const chipNodes = filteredChips.map((item, index) => (
    <Styled.ChipListWrapper className="chip_list_wrapper" key={item.id ?? `${item.key}-${item.chipLabel}-${index}`}>
      <Styled.Chip className="chip" aria-label={`filter by ${item.chipLabel}`}>
        <Styled.ChipText className="chip_text">{item.chipLabel}</Styled.ChipText>
        {!hideDelete && (
          <Styled.DeleteChip
            data-html2canvas-ignore
            id={uniqueId()}
            onClick={() => deleteChips(item, index)}
            aria-label={`Delete filter by ${item.chipLabel}`}
          >
            <Img size={[11, 11]} src={REMOVE_ICON} aria-hidden />
            <Styled.HiddenText>close</Styled.HiddenText>
          </Styled.DeleteChip>
        )}
      </Styled.Chip>
    </Styled.ChipListWrapper>
  ));

  // Inline variant (the Retailers-list filter bar): a single horizontally-scrolling strip of chips
  // with a trailing "Clear all" text link. No "Selected filtering:" title and no Show more/less toggle.
  if (inline) {
    return (
      <Styled.Wrapper className="chips_wrapper" withoutScroll inline>
        <Styled.InlineScroll className="chips_scroll">{chipNodes}</Styled.InlineScroll>
        {hasChips && (
          <Styled.ClearLink type="button" data-html2canvas-ignore onClick={handleClearAllFilters} id={uniqueId()}>
            Clear all
          </Styled.ClearLink>
        )}
      </Styled.Wrapper>
    );
  }

  return (
    <Styled.Wrapper className="chips_wrapper" isShowAll={isShowAll} withoutScroll={withoutScroll}>
      <Styled.ChipListWrapper className="chip_list_wrapper" ref={refList}>
        <Styled.Title data-html2canvas-ignore>Selected filtering:</Styled.Title>
        {hasChips && (
          <Styled.ClearFilters data-html2canvas-ignore onClick={handleClearAllFilters} id={uniqueId()}>
            Clear All
          </Styled.ClearFilters>
        )}
        {!isChipListDisabled && (
          <Styled.ToggleShowFilters
            data-html2canvas-ignore
            onClick={toggleIsShowAll}
            disabled={isChipListDisabled}
            aria-label={isShowAll ? 'Show less' : 'Show more'}
            id={uniqueId()}
          >
            {isShowAll ? 'Show less' : 'Show more'}
          </Styled.ToggleShowFilters>
        )}
        {chipNodes}
      </Styled.ChipListWrapper>
    </Styled.Wrapper>
  );
};

ChipList.defaultProps = {
  withoutScroll: false,
  hideDelete: false,
  isFilterModal: false,
  onCountChange: undefined,
  inline: false,
};
export { ChipList };
